/**
 * AI Agent 채팅 계약 (ADR 0026 · Issue #18 · Phase A)
 * --------------------------------------------------------------------------
 * `POST /api/ai/chat` 하나가 브라우저의 유일한 통로입니다. 브라우저는 Holmes를
 * 모르고, Holmes의 이벤트 이름도 보지 않습니다.
 *
 * 규칙
 * - **UID 우선.** entity는 화면이 **응답에서 읽은** UID로 지목합니다. URL의 이름이
 *   아닙니다 — 이름이 같아도 재생성된 Pod는 다른 인스턴스입니다. (README §5)
 * - **닫힌 요청.** 서버가 모르는 필드를 거절합니다. Phase B/C의 action payload는
 *   모르는 필드라 같은 자리에서 400이 됩니다.
 * - **이력은 요청에 없습니다.** 대화의 소유자는 대시보드이고 서버가 저장소에서
 *   유계로 읽습니다 — 브라우저가 프롬프트를 조작할 경로를 만들지 않습니다.
 * - **문장이 아니라 사유.** 오류는 `AIChatErrorCode`이고 문구는 화면이 고릅니다. (ADR 0025)
 *
 * Go 정본은 apps/api/internal/contract/aichat.go,
 * OpenAPI 정본은 packages/contracts/openapi.yaml입니다.
 */
import type { RangeKey } from "./index";
import type { AIActionProposal } from "./agent-action";
import type { AIActionApprovalRef } from "./agent-action-execution";

/** 유계 상한. Go·OpenAPI와 같은 숫자여야 하며 parity 테스트가 대조합니다. */
export const AI_CHAT_LIMITS = {
  /** 질문 한 건의 최대 길이(rune) */
  questionRunes: 2000,
  /** sessionId·UID 같은 식별자의 최대 길이 */
  idLength: 128,
  /** 서버가 Holmes로 넘기는 이력 메시지 수 상한 */
  historyMessages: 20,
  /** 최종 답변 바이트 상한 */
  answerBytes: 32768,
  /** 진행 상태 한 줄의 길이 상한(rune) */
  progressLabelRunes: 120,
  /** 최종 답변에 붙는 딥링크 수 상한 */
  references: 20,
} as const;

/** `POST /api/ai/chat` 요청. 모르는 필드는 서버가 거절합니다. */
export interface AIChatRequest {
  /** 비어 있으면 새 대화입니다. 있으면 요청자 소유여야 합니다. */
  sessionId?: string;
  /** 1..AI_CHAT_LIMITS.questionRunes */
  question: string;
  entity: AIEntityContext;
}

/** "무엇에 대한 대화인가". 신원은 UID이고 이름은 표시용이라 계약에 없습니다. */
export interface AIEntityContext {
  clusterId: string;
  namespace?: string;
  /** 화면이 응답에서 읽은 Workload UID. URL의 이름이 아닙니다. */
  workloadUid?: string;
  /** 화면이 응답에서 읽은 Pod UID. */
  podUid?: string;
  /** 화면의 시간 범위 프리셋. custom은 이 경로에 없습니다. */
  range?: Exclude<RangeKey, "custom">;
}

/** 브라우저로 나가는 **닫힌** SSE 이벤트 집합. */
export const AI_CHAT_EVENT_TYPES = ["session", "progress", "message", "final", "error"] as const;
export type AIChatEventType = (typeof AI_CHAT_EVENT_TYPES)[number];

/** 진행 단계의 닫힌 집합. 문구는 화면이 고릅니다. */
export const AI_PROGRESS_STEPS = ["reading", "thinking", "answering"] as const;
export type AIProgressStep = (typeof AI_PROGRESS_STEPS)[number];

/** 스트림의 첫 프레임. 세션·턴 식별자를 확정합니다. */
export interface AISessionEvent {
  sessionId: string;
  turnId: string;
}

/** 진행 상태 한 줄. 질의 원문·결과·내부 주소를 담지 않습니다. */
export interface AIProgressEvent {
  step: AIProgressStep;
  label?: string;
}

/** 답변 조각. */
export interface AIMessageEvent {
  delta: string;
}

/** 완결된 답변. */
export interface AIFinalEvent {
  messageId: string;
  content: string;
  /** 서버가 **실제로 조회에 성공한** 대상만. 모델이 지어낸 URL이 아닙니다. */
  references?: AIResourceRef[];
  truncated?: boolean;
  /**
   * 서버가 `action.v1` 계약으로 **재검증하고 이 턴의 권한·근거에 결합한** 제안만.
   * (ADR 0027 Phase B)
   *
   * 없으면 필드 자체가 없습니다 — Phase A 응답과 바이트가 같습니다. 제안 전용이며
   * 승인·실행 표면은 이 계약 어디에도 없습니다.
   */
  actions?: AIActionProposal[];
  /**
   * 승인 가능한 제안의 **서버 발급 레코드 id**. (ADR 0028 Phase C)
   *
   * Phase C가 꺼진 배포·권한 없는 사용자에게는 필드 자체가 없고, 그때 카드는
   * Phase B 그대로 제안 전용입니다. 화면은 여기 실린 `recordId`만 승인 요청에
   * 실어 보냅니다 — 모델이 쓴 제안 id로 실행 대상을 고를 수 없습니다.
   */
  approvals?: AIActionApprovalRef[];
}

/**
 * 화면 내부 딥링크로만 렌더링되는 리소스 참조.
 * 자유 URL이 아닙니다 — UI가 kind와 식별자로 자기 라우트를 만듭니다.
 */
export interface AIResourceRef {
  kind: "pod" | "workload";
  clusterId: string;
  namespace: string;
  name: string;
  uid: string;
  /** kind가 workload일 때의 Kubernetes Kind */
  workloadKind?: string;
}

/** 스트림 안에서 끝난 실패. 문장이 아니라 안정 사유입니다. */
export interface AIErrorEvent {
  code: AIChatErrorCode;
  retryAfterSeconds?: number;
}

/** AI 경로의 안정 사유 코드. HTTP 오류 본문과 SSE error 이벤트가 같은 집합을 씁니다. */
export const AI_CHAT_ERROR_CODES = [
  "ai_disabled",
  "ai_invalid_request",
  "ai_rate_limited",
  "ai_budget_exceeded",
  "ai_busy",
  "ai_upstream_unavailable",
  "ai_session_not_found",
  "ai_store_unavailable",
  "ai_action_not_supported",
  "ai_replay_unavailable",
] as const;
export type AIChatErrorCode = (typeof AI_CHAT_ERROR_CODES)[number];

/** 이벤트 타입 → payload. 화면은 이 표 밖의 조합을 만들 수 없습니다. */
export type AIChatEvent =
  | { type: "session"; data: AISessionEvent }
  | { type: "progress"; data: AIProgressEvent }
  | { type: "message"; data: AIMessageEvent }
  | { type: "final"; data: AIFinalEvent }
  | { type: "error"; data: AIErrorEvent };

export function isAIChatEventType(value: unknown): value is AIChatEventType {
  return typeof value === "string" && (AI_CHAT_EVENT_TYPES as readonly string[]).includes(value);
}

export function isAIChatErrorCode(value: unknown): value is AIChatErrorCode {
  return typeof value === "string" && (AI_CHAT_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * 리소스 참조를 **엄격하게** 검증합니다.
 *
 * 화면은 이 함수를 통과한 값만 딥링크로 만듭니다. 통과하지 못한 참조는 링크가
 * 아니라 사라집니다 — 모델이 지어낸 값으로 라우트를 만들면 404이거나, 더 나쁘게는
 * 사용자가 볼 수 없는 대상을 가리키는 링크가 됩니다.
 */
export function isSafeResourceRef(value: unknown): value is AIResourceRef {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const ref = value as Record<string, unknown>;
  if (ref.kind !== "pod" && ref.kind !== "workload") return false;
  for (const key of ["clusterId", "namespace", "name", "uid"] as const) {
    const field = ref[key];
    if (typeof field !== "string" || field.length === 0 || field.length > AI_CHAT_LIMITS.idLength) return false;
    /* 경로·질의·마크업 어디에 실려도 안전한 집합만 허용합니다. */
    if (!/^[A-Za-z0-9._-]+$/.test(field)) return false;
  }
  if (ref.workloadKind !== undefined) {
    if (typeof ref.workloadKind !== "string" || !/^[A-Za-z]{1,32}$/.test(ref.workloadKind)) return false;
  }
  return true;
}

/**
 * 진행 라벨의 **닫힌 집합**.
 *
 * 서버는 위임 조회 엔드포인트 이름을 이 코드로 바꿔서만 내보냅니다. upstream의
 * tool 이름·설명·출력은 이 필드에 도달하지 않고, 모르는 호출은 빈 라벨이 됩니다.
 */
export const AI_PROGRESS_LABELS = ["workload", "logs", "metrics", "events", "alerts"] as const;
export type AIProgressLabel = (typeof AI_PROGRESS_LABELS)[number] | "";

/**
 * 진행 라벨을 닫힌 집합으로만 받아들입니다.
 *
 * 자유 문자열을 다듬어서 통과시키지 않습니다 — 다듬는 경로가 있으면 중간
 * 프록시나 미래의 서버 버전이 보낸 tool 원문 한 줄이 그대로 화면에 도달합니다.
 * 집합 밖은 값이 아니라 **없음**이고, 화면은 step으로 문구를 고릅니다.
 */
export function safeProgressLabel(raw: unknown): AIProgressLabel {
  return typeof raw === "string" && (AI_PROGRESS_LABELS as readonly string[]).includes(raw)
    ? (raw as AIProgressLabel)
    : "";
}
